import React from 'react';

// Shared onError handler for item images. Nulls out onerror first to prevent
// infinite request loops when the fallback itself cannot be found.
export const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>): void => {
  const img = e.currentTarget;
  img.onerror = null;
  img.src = '/img/no_img.png';
};

// Normalize curly apostrophes to straight apostrophes to match filenames.
const normalizeItemName = (name: string): string =>
  name.replace(/\u2019/g, '\u0027'); // curly ' → straight '

// Map itemCategory to the corresponding items subfolder.
const categoryToFolder = (category: string): string => {
  switch (category?.toLowerCase()) {
    case 'weapon':
    case 'shield':    return 'weapon';
    case 'armor':     return 'armor';
    case 'accessory': return 'accessory';
    case 'material':  return 'material';
    case 'potion':    return 'potion';
    case 'enchant':
    case 'misc':
    case 'miscellaneous': return 'misc';
    case 'rune':      return 'runes';
    case 'knowledge': return 'knowledge';
    default:          return '';
  }
};

export const getIngredientIconSrc = (name: string, category = 'Material'): string => {
  if (!name) return '/img/no_img.png';
  const folder = categoryToFolder(category);
  const fileName = normalizeItemName(name);
  return folder
    ? `/img/items/${folder}/${fileName}.png`
    : `/img/items/${fileName}.png`;
};

// Maps profession names to icon filenames in /img/items/knowledge/
const PROFESSION_ICON_MAP: Record<string, string> = {
  'Alchemy': 'alchemy',
  'Jeweler': 'jeweler',
  'Armorsmithing': 'armorsmithing',
  'Weaponsmithing': 'weaponsmithing',
};

export const getItemImageSrc = (item: any): string => {
  if (!item) return '/img/no_img.png';

  // Knowledge items: prefer profession-specific icon, fall back to knowledgeType icon.
  if (item.itemCategory?.toLowerCase() === 'knowledge') {
    const profIcon = item.professionName && PROFESSION_ICON_MAP[item.professionName];
    if (profIcon) return `/img/items/knowledge/${profIcon}.png`;
    if (item.knowledgeType) {
      const typeName = normalizeItemName(item.knowledgeType);
      return `/img/items/knowledge/${typeName}.png`;
    }
  }

  // Use item.img if available and valid (not default), otherwise fall back to item.name.
  let imageName = item.img;

  if (!imageName || imageName === 'default' || imageName === 'default.png') {
    imageName = item.name;
  }

  if (!imageName) return '/img/no_img.png';

  // Normalize curly apostrophes to match actual filenames.
  imageName = normalizeItemName(imageName);

  // Handle missing extension.
  if (!imageName.toLowerCase().endsWith('.png')) {
    imageName += '.png';
  }

  // If img already contains a path (e.g. "img/items/runes/Green_S1_Shape1.png"),
  // don't double-prepend /img/items/.
  if (imageName.includes('/')) {
    return `/${imageName}`;
  }

  // Normalize underscores to spaces — filenames on disk use spaces, DB img fields may use underscores.
  imageName = imageName.replace(/_/g, ' ');

  // Use itemCategory to route into the correct subfolder.
  const folder = categoryToFolder(item.itemCategory || '');
  if (folder) {
    return `/img/items/${folder}/${imageName}`;
  }

  return `/img/items/${imageName}`;
};
